// useGetLiveStatus — single source of truth for the Get-Live Companion and the
// A2P trial banner. Derives ALL state from real data (nothing stored): per-campaign
// launch readiness, company-profile completeness, A2P progress, and trial days left.
//
// See design_handoff_get_live_companion/WIRING.md. Reuses the same signals the
// Campaigns page and Settings page use, so "green" means the same thing everywhere.
import { useState, useEffect, useCallback } from 'react';
import supabase from '../../lib/supabaseClient';
import { deriveCampaignSetup, stepsDone } from '../../pages/campaign-console/consoleData';
import { getA2PProgress, isA2PApproved } from '../settings/surfox-settings/hooks/useSettingsData';
import { computeA2PStage, a2pBannerContent } from '../../lib/a2pStage';

const TRIAL_DAYS = 14; // All trials are 14 days (confirmed by product).
const DAY_MS = 24 * 60 * 60 * 1000;

// Campaign columns the readiness model reads (mirrors fetchCampaigns + deriveCampaignSetup).
// A field deriveCampaignSetup reads that is missing here comes back undefined, not an
// error, and every campaign in the Get-Live hub silently goes red. The web chat rules
// added 2026-09-10 read only columns already listed (talk_track_type,
// custom_talk_track_id, notification_email).
const CAMPAIGN_COLS = [
  'id', 'tenant_id', 'name', 'ai_on', 'is_active', 'first_activated_at',
  'ai_representative_name', 'phone_number_id', 'timezone', 'notification_email',
  'talk_track_type', 'custom_talk_track_id',
  'initial_tone', 'initial_persona', 'engagement_tone', 'engagement_persona',
  'ai_instruction_initial', 'require_qualification_before_transfer', 'qualification_questions',
].join(', ');

function rawSetting(value) {
  return (value && typeof value === 'object' && 'value' in value) ? value.value : value;
}

export default function useGetLiveStatus(tenantId) {
  const [state, setState] = useState({
    loading: true,
    campaigns: [],          // [{ id, name, ai_on, done, total: 5, setup }]
    campaign: null,         // the single go-live campaign the hub shows
    company: { isComplete: false },
    knowledge: { isComplete: false },
    leads: { isComplete: false },
    availability: { isComplete: false },
    a2p: { isComplete: false, brandRegistered: false, progress: null },
    trial: { isTrialing: false, daysLeft: TRIAL_DAYS, total: TRIAL_DAYS },
    onboardingComplete: false,
    hasEverLaunched: false,  // write-once first_activated_at — retires the hub for good
    leftCount: 0,
  });

  const fetchAll = useCallback(async () => {
    if (!tenantId) { setState(s => ({ ...s, loading: false })); return; }
    try {
      const [
        tenantRes, settingsRes, campaignsRes, knowledgeRes, linksRes,
        a2pBrandRes, a2pCampaignsRes, phonesRes, calendarRes, a2pProfileRes, leadsRes,
      ] = await Promise.all([
        supabase.from('tenants')
          .select('name, industry, subscription_status, billing_cycle_start_date, created_at, widget_campaign_id, voice_ai_campaign_id, messenger_campaign_id, onboarding_complete')
          .eq('id', tenantId).single(),
        supabase.from('platform_settings').select('key, value').eq('tenant_id', tenantId)
          .in('key', ['company_name', 'industry', 'officeOpenHour', 'officeCloseHour', 'officeDays']),
        supabase.from('campaigns').select(CAMPAIGN_COLS).eq('tenant_id', tenantId).eq('archived', false),
        // file_name distinguishes a real customer upload from the inert onboarding
        // template row (source_type='master_kb', file_name NULL) — see
        // reference_onboarding_kb_template_only: KB "done" = they uploaded a doc.
        supabase.from('knowledge_base').select('id, tenant_id, is_master, file_name').eq('tenant_id', tenantId),
        supabase.from('campaign_knowledge_links').select('campaign_id, knowledge_id'),
        supabase.from('a2p_brands').select('status, twilio_brand_sid').eq('tenant_id', tenantId).maybeSingle(),
        supabase.from('a2p_campaigns').select('id, status').eq('tenant_id', tenantId),
        supabase.from('phone_numbers').select('id, messaging_service_sid').eq('tenant_id', tenantId),
        supabase.from('calendar_settings')
          .select('availability_mode, custom_availability, google_connected, outlook_connected')
          .eq('tenant_id', tenantId).maybeSingle(),
        // Customer profile — first A2P stage; needed so the banner mirrors /admin/a2p.
        // consent_verified drives the "add SMS consent to your site" held state.
        supabase.from('customer_profiles').select('status, consent_verified, website').eq('tenant_id', tenantId).maybeSingle(),
        // Lead count only — head request, no rows pulled.
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      ]);

      const tenant = tenantRes.data || {};
      const settings = {};
      (settingsRes.data || []).forEach(({ key, value }) => { settings[key] = rawSetting(value); });

      // Company profile — same rule as useSettingsData (WIRING §4).
      const companyName = settings.company_name;
      const industry = tenant.industry || settings.industry;
      const companyIsComplete = !!(companyName && industry);

      // Knowledge base — "done" when the customer has uploaded a real document
      // (file_name set). The onboarding template row has file_name NULL and does
      // NOT count (reference_onboarding_kb_template_only).
      const knowledgeIsComplete = (knowledgeRes.data || []).some(k => k.file_name);

      // Leads — "done" once they have imported at least one lead.
      const leadsIsComplete = (leadsRes.count || 0) > 0;

      // Business hours configured? (Decision B — folded into Setup.)
      const businessHoursConfigured = !!(settings.officeOpenHour && settings.officeCloseHour && settings.officeDays);
      const businessHoursByTenant = { [tenantId]: businessHoursConfigured };

      // Per-campaign readiness via the shared helper.
      const links = {};
      (linksRes.data || []).forEach(l => { (links[l.campaign_id] ||= []).push(l.knowledge_id); });
      const ctx = {
        widgetCampaignId: tenant.widget_campaign_id || null,
        voiceCampaignId: tenant.voice_ai_campaign_id || null,
        messengerCampaignId: tenant.messenger_campaign_id || null,
        isGlobalAdmin: false,
        industries: { [tenantId]: industry || '' },
        knowledgeAssets: knowledgeRes.data || [],
        selectedKnowledgeAssets: links,
        businessHoursByTenant,
      };
      const campaigns = (campaignsRes.data || []).map(c => {
        const setup = deriveCampaignSetup(c, ctx);
        return {
          id: c.id, name: c.name, ai_on: !!c.ai_on, total: 5, done: stepsDone(setup), setup,
          firstActivatedAt: c.first_activated_at || null,
        };
      });

      // Has this tenant EVER got a campaign live? Write-once at first activation
      // (api_routes/campaigns.js) and never cleared, so unlike ai_on it can't go
      // backwards when a campaign is paused or a new draft appears. This is the
      // Get-Live hub's retirement signal: the hub teaches first launch, and once
      // that's done it must stay gone. Same expression admin-onboarding.js uses
      // for hasCampaignActivated, and the same signal send-scheduled-emails uses
      // to suppress the "no campaign" nag — one definition of "they made it".
      const hasEverLaunched = campaigns.some(c => !!c.firstActivatedAt);

      // Availability — a "confirm when your AI can book" nudge, NOT a hard gate:
      // scheduling-service already falls back to the tenant's business hours
      // (Mon–Fri 9–5 default) when no calendar_settings row exists, so the AI can
      // book regardless. "Done" just means they've opened the page and have a usable
      // config: calendar mode needs a connected calendar (free/busy source), custom
      // mode needs ≥1 enabled day window. No row = never reviewed availability.
      const cal = calendarRes.data;
      let availabilityIsComplete = false;
      if (cal) {
        if (cal.availability_mode === 'calendar') {
          availabilityIsComplete = !!(cal.google_connected || cal.outlook_connected);
        } else {
          const days = cal.custom_availability && typeof cal.custom_availability === 'object' ? cal.custom_availability : {};
          availabilityIsComplete = Object.values(days).some(d => d && d.enabled);
        }
      }

      // A2P — reuse the Settings progress calc (drives the Companion step list).
      const progress = getA2PProgress(a2pBrandRes.data || null, a2pCampaignsRes.data || [], phonesRes.data || []);

      // Campaign-fire is HELD when the brand is approved but the customer's website
      // is missing the SMS consent language carriers verify (consent_verified=false),
      // so refresh-a2p-status hasn't created the campaign yet. Surfaced as an
      // actionable "add consent" item in the tracker.
      const a2pConsentBlocked = isA2PApproved(a2pBrandRes.data?.status)
        && (a2pCampaignsRes.data || []).length === 0
        && a2pProfileRes.data?.consent_verified === false;
      // A2P stage — mirrors /admin/a2p exactly (profile → brand → campaign), so the
      // banner can speak the same language as the admin dashboard.
      const a2pStage = computeA2PStage({
        profile: a2pProfileRes.data || null,
        brand: a2pBrandRes.data || null,
        campaigns: a2pCampaignsRes.data || [],
      });
      // A2P is a "to-do" only when the user must act (register / create campaign /
      // fix a rejection). Pending carrier review is NOT a to-do — it's captured
      // during Surf onboarding and clears automatically — so it must not inflate
      // leftCount or trigger action-y copy in the Go-Live hub.
      const a2pContent = a2pBannerContent(a2pStage);
      const a2pActionable = !!a2pContent && (a2pContent.tone === 'action' || a2pContent.tone === 'failed');

      // Trial days left (Decision C). No trial_end column → compute from the
      // trial-start field (billing_cycle_start_date, set at signup) + 14 days.
      const isTrialing = tenant.subscription_status === 'trialing';
      const startStr = tenant.billing_cycle_start_date || tenant.created_at;
      let daysLeft = TRIAL_DAYS;
      if (startStr) {
        const end = new Date(startStr).getTime() + TRIAL_DAYS * DAY_MS;
        daysLeft = Math.max(0, Math.min(TRIAL_DAYS, Math.ceil((end - Date.now()) / DAY_MS)));
      }

      // Go-live tracks ONE campaign — the customer's onboarding campaign (a fresh
      // tenant has exactly one). If several exist, take the first still needing a
      // launch, else the first campaign. The hub never lists more than this one.
      const primaryCampaign = campaigns.find(c => !c.ai_on) || campaigns[0] || null;
      const campaignStepLeft = primaryCampaign && !primaryCampaign.ai_on ? 1 : 0;
      const leftCount = (companyIsComplete ? 0 : 1) + (knowledgeIsComplete ? 0 : 1)
        + (leadsIsComplete ? 0 : 1) + (availabilityIsComplete ? 0 : 1)
        + campaignStepLeft + (a2pActionable ? 1 : 0);

      setState({
        loading: false,
        campaigns,
        campaign: primaryCampaign, // the single go-live campaign the hub shows
        company: { isComplete: companyIsComplete },
        knowledge: { isComplete: knowledgeIsComplete },
        leads: { isComplete: leadsIsComplete },
        availability: { isComplete: availabilityIsComplete },
        a2p: {
          isComplete: progress.isComplete,
          brandRegistered: !!a2pBrandRes.data?.twilio_brand_sid,
          stage: a2pStage,   // canonical stage, mirrors /admin/a2p — drives the banner
          actionable: a2pActionable, // true only when the user must do something
          consentBlocked: a2pConsentBlocked,
          progress,
        },
        trial: { isTrialing, daysLeft, total: TRIAL_DAYS },
        onboardingComplete: !!tenant.onboarding_complete,
        hasEverLaunched,
        leftCount,
      });
    } catch (err) {
      console.error('useGetLiveStatus failed:', err?.message);
      setState(s => ({ ...s, loading: false }));
    }
  }, [tenantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { ...state, refetch: fetchAll };
}
